import React, { useState, useEffect, useMemo, useCallback } from 'react';
import '../styles/Experiments.css';

// End of May 21, 2026 in Arizona time (MST = UTC-7, no DST).
// Past this timestamp the tempradio command is no longer relevant
// and we render the "experiment ended" state.
const STOPTIME_UNIX = Math.floor(Date.UTC(2026, 4, 22, 7, 0, 0) / 1000);

const DEFAULTS = Object.freeze({
  freq: '908.205',
  bw: '62.5',
  sf: '9',
  cr: '8',
});

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatCountdown(secondsLeft) {
  if (secondsLeft <= 0) return null;
  const d = Math.floor(secondsLeft / 86400);
  const h = Math.floor((secondsLeft % 86400) / 3600);
  const m = Math.floor((secondsLeft % 3600) / 60);
  const s = secondsLeft % 60;
  return { d, h, m, s };
}

const Experiments = () => {
  const now = useNow(1000);
  const [params, setParams] = useState(DEFAULTS);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const secondsLeft = Math.max(0, STOPTIME_UNIX - now);
  const minutesLeft = Math.floor(secondsLeft / 60);
  const expired = secondsLeft === 0;
  const countdown = formatCountdown(secondsLeft);

  const command = useMemo(
    () => `tempradio ${params.freq},${params.bw},${params.sf},${params.cr},${minutesLeft}`,
    [params, minutesLeft]
  );

  const handleParamChange = (key) => (e) => {
    setParams((p) => ({ ...p, [key]: e.target.value }));
  };

  const resetParams = useCallback(() => setParams(DEFAULTS), []);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(command);
      } else {
        const ta = document.createElement('textarea');
        ta.value = command;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard rejected (no permission / insecure context).
      // Leave copied=false; the user can still select the text manually.
    }
  }, [command]);

  const stopDate = new Date(STOPTIME_UNIX * 1000);
  const stopLabel = stopDate.toLocaleString(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'America/Phoenix',
  });

  return (
    <>
      <section className="hero is-primary experiments-hero">
        <div className="hero-body">
          <div className="container has-text-centered">
            <span className="tag is-warning is-medium mb-4 experiments-badge">
              <span className="icon"><i className="fas fa-flask" /></span>
              <span>Active experiment</span>
            </span>
            <h1 className="title is-1 has-text-white">Experiments</h1>
            <h2 className="subtitle is-4 has-text-white">
              Time-bounded RF tests for the Arizona mesh
            </h2>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-8-desktop is-10-tablet">

              {/* Description */}
              <div className="content has-text-centered mb-5">
                <p className="is-size-5">
                  We're running a community-wide LoRa test. Paste the command
                  below into your MeshCore node's console to temporarily
                  modify your radio's settings for the duration of the experiment.
                </p>
                <p className="is-size-6 has-text-grey">
                  Your node will automatically revert to its previous
                  configuration when the timer expires &mdash; no manual cleanup
                  needed.
                </p>
              </div>

              {/* Command card */}
              <div className={`box experiments-card ${expired ? 'is-expired' : ''}`}>
                <div className="level is-mobile mb-4">
                  <div className="level-left">
                    <div>
                      <p className="heading has-text-grey-light mb-1">
                        {expired ? 'Experiment ended' : 'Your command'}
                      </p>
                      <p className="experiments-eyebrow">
                        <span className="icon has-text-success-light">
                          <i className={`fas ${expired ? 'fa-circle-xmark' : 'fa-circle-dot fa-beat-fade'}`} />
                        </span>
                        <span className="ml-2">
                          {expired
                            ? 'No longer accepting test traffic'
                            : 'Live — updates every minute'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <pre className="experiments-command" aria-label="tempradio command">
                  <code>{command}</code>
                </pre>

                <div className="field is-grouped is-grouped-multiline mt-4">
                  <div className="control">
                    <button
                      type="button"
                      className={`button is-primary is-medium ${copied ? 'is-success' : ''}`}
                      onClick={handleCopy}
                      disabled={expired}
                    >
                      <span className="icon">
                        <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} />
                      </span>
                      <span>{copied ? 'Copied!' : 'Copy command'}</span>
                    </button>
                  </div>
                  <div className="control">
                    <button
                      type="button"
                      className="button is-medium is-light"
                      onClick={() => setShowAdvanced((v) => !v)}
                      aria-expanded={showAdvanced}
                    >
                      <span className="icon">
                        <i className={`fas ${showAdvanced ? 'fa-chevron-up' : 'fa-sliders'}`} />
                      </span>
                      <span>{showAdvanced ? 'Hide parameters' : 'Customize parameters'}</span>
                    </button>
                  </div>
                </div>

                {showAdvanced && (
                  <div className="experiments-advanced mt-5 pt-4">
                    <div className="columns is-multiline is-variable is-3">
                      <ParamField label="Frequency (MHz)" value={params.freq} onChange={handleParamChange('freq')} placeholder={DEFAULTS.freq} />
                      <ParamField label="Bandwidth (kHz)" value={params.bw} onChange={handleParamChange('bw')} placeholder={DEFAULTS.bw} />
                      <ParamField label="Spreading factor" value={params.sf} onChange={handleParamChange('sf')} placeholder={DEFAULTS.sf} />
                      <ParamField label="Coding rate" value={params.cr} onChange={handleParamChange('cr')} placeholder={DEFAULTS.cr} />
                    </div>
                    <div className="has-text-right mt-2">
                      <button type="button" className="button is-small is-light" onClick={resetParams}>
                        <span className="icon"><i className="fas fa-rotate-left" /></span>
                        <span>Reset to defaults</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Countdown */}
              <div className="experiments-countdown mt-5">
                {expired ? (
                  <div className="notification is-light is-warning has-text-centered">
                    <p className="is-size-5">
                      <strong>This experiment closed on {stopLabel}.</strong>
                    </p>
                    <p className="is-size-6 mt-2">
                      Check back later — we run new tests every few weeks.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="heading has-text-grey has-text-centered">Time remaining</p>
                    <div className="experiments-clock">
                      <ClockCell value={countdown.d} label={countdown.d === 1 ? 'day' : 'days'} />
                      <ClockCell value={String(countdown.h).padStart(2, '0')} label="hours" />
                      <ClockCell value={String(countdown.m).padStart(2, '0')} label="minutes" />
                      <ClockCell value={String(countdown.s).padStart(2, '0')} label="seconds" />
                    </div>
                    <p className="has-text-centered has-text-grey mt-3 is-size-7">
                      Ends {stopLabel} (Arizona time)
                    </p>
                  </>
                )}
              </div>

              {/* How-to */}
              <div className="content mt-6">
                <h3 className="title is-5 has-text-primary">How to apply</h3>
                <ol>
                  <li>Login to your repeater.</li>
                  <li>Go to the CLI.</li>
                  <li>Paste in the command.</li>
                </ol>
                <p className="is-size-6 has-text-grey-light">
                  If you have any questions, ask on{' '}
                  <a
                    href="https://discord.gg/RPj3b7ZUz9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="has-text-link"
                  >
                    Discord
                  </a>
                  .
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const ParamField = ({ label, value, onChange, placeholder }) => (
  <div className="column is-half">
    <div className="field">
      <label className="label is-small has-text-grey-light">{label}</label>
      <div className="control">
        <input
          className="input is-dark experiments-input"
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
      </div>
    </div>
  </div>
);

const ClockCell = ({ value, label }) => (
  <div className="experiments-clock-cell">
    <div className="experiments-clock-value">{value}</div>
    <div className="experiments-clock-label">{label}</div>
  </div>
);

export default Experiments;
